import type {
  AttendeeNoteConflict,
  AttendeeNoteMarker,
  AttendeeNoteValues,
  AttendeePrivateNote,
} from "@verity-connect/contracts";

const DATABASE_NAME = "wvcea-connect-private-v1";
const STORE_NAME = "session-notes";
const PURGE_PREFIX = "wvcea:private-notes-purged:";

export interface PendingNoteMutation {
  id: string;
  baseRevision: number;
  clientUpdatedAt: string;
}

export interface LocalPrivateNote {
  key: string;
  identityScope: string;
  eventId: string;
  itemId: string;
  values: AttendeeNoteValues;
  localRevision: number;
  serverRevision: number;
  pending: PendingNoteMutation | null;
  conflict: AttendeeNoteConflict | null;
  savedAt: string;
  synchronizedAt: string | null;
}

export function privateNoteKey(identityScope: string, eventId: string, itemId: string): string {
  return `${identityScope}:${eventId}:${itemId}`;
}

export function blankPrivateNote(identityScope: string, eventId: string, itemId: string): LocalPrivateNote {
  return {
    key: privateNoteKey(identityScope, eventId, itemId),
    identityScope,
    eventId,
    itemId,
    values: { body: "", markers: [] },
    localRevision: 0,
    serverRevision: 0,
    pending: null,
    conflict: null,
    savedAt: new Date(0).toISOString(),
    synchronizedAt: null,
  };
}

export function editPrivateNote(note: LocalPrivateNote, values: AttendeeNoteValues, now: string): LocalPrivateNote {
  return {
    ...note,
    values,
    localRevision: note.localRevision + 1,
    pending: { id: crypto.randomUUID(), baseRevision: note.serverRevision, clientUpdatedAt: now },
    conflict: null,
    savedAt: now,
  };
}

export function acceptRemoteNote(local: LocalPrivateNote | undefined, remote: AttendeePrivateNote, identityScope: string, eventId: string): LocalPrivateNote {
  if (local?.pending || local?.conflict) return local;
  return {
    key: privateNoteKey(identityScope, eventId, remote.itemId),
    identityScope,
    eventId,
    itemId: remote.itemId,
    values: remote.values,
    localRevision: local?.localRevision ?? 0,
    serverRevision: remote.revision,
    pending: null,
    conflict: null,
    savedAt: remote.clientUpdatedAt,
    synchronizedAt: remote.serverUpdatedAt,
  };
}

export function markerLabel(kind: AttendeeNoteMarker["kind"]): string {
  if (kind === "key_idea") return "Key idea";
  if (kind === "question") return "Question";
  return "Next action";
}

export async function listPrivateNotes(identityScope: string, eventId: string): Promise<LocalPrivateNote[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).getAll();
    request.onerror = () => reject(request.error ?? new Error("Private notes could not be read from this device."));
    request.onsuccess = () => resolve((request.result as LocalPrivateNote[]).filter((note) =>
      note.identityScope === identityScope
      && note.eventId === eventId
      && !purgedAfter(note.identityScope, Date.parse(note.savedAt)),
    ));
  });
}

export async function putPrivateNote(note: LocalPrivateNote): Promise<void> {
  if (purgedAfter(note.identityScope, Date.parse(note.savedAt))) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("The private note could not be saved on this device."));
    transaction.objectStore(STORE_NAME).put(note);
  });
}

/** Remove only this person's device copies after the server confirms account deletion. */
export async function deletePrivateNotes(identityScope: string): Promise<void> {
  try { localStorage.setItem(`${PURGE_PREFIX}${identityScope}`, String(Date.now())); } catch { /* IndexedDB removal still runs. */ }
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      for (const note of request.result as LocalPrivateNote[]) {
        if (note.identityScope === identityScope) store.delete(note.key);
      }
    };
    request.onerror = () => reject(request.error ?? new Error("Private notes could not be cleared from this device."));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Private notes could not be cleared from this device."));
  });
}

export async function adoptGuestPrivateNotes(identityScope: string, eventId: string): Promise<void> {
  if (identityScope === "guest") return;
  const startedAt = Date.now();
  const [guestNotes, accountNotes] = await Promise.all([
    listPrivateNotes("guest", eventId),
    listPrivateNotes(identityScope, eventId),
  ]);
  const accountByItem = new Map(accountNotes.map((note) => [note.itemId, note]));
  const database = await openDatabase();
  if (purgedAfter(identityScope, startedAt)) return;
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    for (const guest of guestNotes) {
      const account = accountByItem.get(guest.itemId);
      const values = account ? combineValues(account.values, guest.values) : guest.values;
      const base = account ?? blankPrivateNote(identityScope, eventId, guest.itemId);
      const now = new Date().toISOString();
      store.put(editPrivateNote({ ...base, identityScope, key: privateNoteKey(identityScope, eventId, guest.itemId) }, values, now));
      store.delete(guest.key);
    }
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Device-only notes could not be connected to this account."));
  });
}

function purgedAfter(identityScope: string, writtenAt: number): boolean {
  if (identityScope === "guest") return false;
  try {
    const purgedAt = Number(localStorage.getItem(`${PURGE_PREFIX}${identityScope}`) ?? "0");
    return Number.isFinite(purgedAt) && purgedAt >= writtenAt;
  } catch {
    return false;
  }
}

function combineValues(account: AttendeeNoteValues, guest: AttendeeNoteValues): AttendeeNoteValues {
  const body = !account.body.trim() ? guest.body : !guest.body.trim() || account.body === guest.body ? account.body : `${account.body}\n\n${guest.body}`;
  const markers = new Map<string, AttendeeNoteMarker>();
  for (const marker of [...account.markers, ...guest.markers]) markers.set(marker.id, marker);
  return { body, markers: [...markers.values()] };
}

function openDatabase(): Promise<IDBDatabase> {
  if (!("indexedDB" in globalThis)) return Promise.reject(new Error("Private note storage is not available in this browser."));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
    };
    request.onerror = () => reject(request.error ?? new Error("Private note storage could not be opened."));
    request.onsuccess = () => resolve(request.result);
  });
}
